import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { useDisplayTheme, useSysTheme } from 'hooks/useUiState'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { Button, CaretDown as CaretDownIcon, Menu } from '@netbasequid/core'
import { selectDashboardInfoCategory } from 'features/dashboardInfo/selectors'
import { selectIsRoleBelowAnalyst } from 'features/config/selectors'
import { setCategory } from 'features/dashboardInfo/dashboardInfoSlice'
import { DashboardCategory } from 'features/dashboardInfo/types'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import { AutomationId } from 'utils/automationUtil'
import { heapTrack, HeapEvent, HeapEventPageValue } from 'utils/heapUtils'
import {
  DASHBOARD_CATEGORY,
  TEMPLATE_CATEGORY,
  LIMITED_DASHBOARD_CATEGORY
} from 'features/dashboardInfo/constants'
import { SystemTheme } from 'hooks/useUiState/types'

import Divider from './Divider'
import { useRetrieveDashboardInfos } from '../hooks'

interface MenuItemClickEvent {
  target: {
    value: DashboardCategory
  }
}

const StyledMenu = styled(Menu)<{ $theme: SystemTheme }>`
  & > div {
    background-color: ${({ $theme }) => $theme.elevation[300].background};
    box-shadow: ${({ $theme }) => $theme.elevation[300].boxShadow};
  }
`

const StyledButton = styled(Button)<{ $theme: SystemTheme }>`
  font-family: var(--large-font-family);
  font-size: ${({ $theme }) => $theme.typescale[16].size};
  line-height: ${({ $theme }) => $theme.typescale[16].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
  color: ${({ $theme }) => $theme.text.primary};
`

const CategoryButton = ({
  style,
  page
}: {
  style?: React.CSSProperties
  page: HeapEventPageValue
}) => {
  const mode = useDisplayTheme()
  const theme = useSysTheme()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const buttonRef = useRef(null)
  const currentCategory = useAppSelector(selectDashboardInfoCategory)
  const isRoleBelowAnalyst = useAppSelector(selectIsRoleBelowAnalyst)
  const dispatch = useAppDispatch()

  useRetrieveDashboardInfos(currentCategory)

  const handleCategoryClick = (val: DashboardCategory) => {
    dispatch(setCategory(val))
    setOpen(false)
    heapTrack(HeapEvent.V4DashboardCategoryClick, { category: val, page })
  }

  return (
    <>
      <StyledButton
        ref={buttonRef}
        mode={mode}
        color="secondary"
        variant="ghost"
        style={style}
        $theme={theme}
        endIcon={<CaretDownIcon fontSize={16} />}
        onClick={() => setOpen(true)}
        data-testid={AutomationId.sidePannel.category}>
        {t(currentCategory)}
      </StyledButton>
      <StyledMenu
        mode={mode}
        $theme={theme}
        open={open}
        disablePortal
        anchorEl={buttonRef.current}
        onClose={() => {
          setOpen(false)
        }}>
        {isRoleBelowAnalyst
          ? Object.values(LIMITED_DASHBOARD_CATEGORY).map((category) => (
              <Menu.MenuItem
                key={category}
                value={category}
                selected={currentCategory === category}
                onClick={(e: MenuItemClickEvent) => handleCategoryClick(e.target.value)}>
                {t(category)}
              </Menu.MenuItem>
            ))
          : [
              Object.values(DASHBOARD_CATEGORY).map((category) => (
                <Menu.MenuItem
                  key={category}
                  value={category}
                  selected={currentCategory === category}
                  onClick={(e: MenuItemClickEvent) => handleCategoryClick(e.target.value)}>
                  {t(category)}
                </Menu.MenuItem>
              )),
              <Divider mode={mode} key="divider" />,
              Object.values(TEMPLATE_CATEGORY).map((category) => (
                <Menu.MenuItem
                  key={category}
                  value={category}
                  selected={currentCategory === category}
                  onClick={(e: MenuItemClickEvent) => handleCategoryClick(e.target.value)}>
                  {t(category)}
                </Menu.MenuItem>
              ))
            ]}
      </StyledMenu>
    </>
  )
}

export default CategoryButton
